import { useCallback, useEffect, useMemo, useState } from 'react';
import { Code2, RefreshCw, Save, SlidersHorizontal } from 'lucide-react';
import { useParams } from 'react-router-dom';
import AntennaLocationConfigEditor from '../components/AntennaLocationConfigEditor';
import BluetoothConfigEditor from '../components/BluetoothConfigEditor';
import CanConfigEditor from '../components/CanConfigEditor';
import GnssConfigEditor from '../components/GnssConfigEditor';
import MneaStreamConfigEditor from '../components/MneaStreamConfigEditor';
import NtripClientConfigEditor from '../components/NtripClientConfigEditor';
import { ConfirmDialog, Toast } from '../components/ui';
import { CONFIG_PATHS, getDeviceConfig, setDeviceConfig } from '../api/api';
import { refreshUnsyncedConfigs, syncAndReboot } from '../hooks/useHubConfigSync';
import styles from './DeviceConfig.module.scss';

const configLabel = path =>
  path
    .split('/')
    .map(part => part.replaceAll('_', ' '))
    .join(' · ');

const configGroup = path => {
  if (path.startsWith('gnss/')) {
    return 'GNSS';
  }
  if (path.startsWith('can/')) {
    return 'CAN bus';
  }
  if (path === 'bluetooth') {
    return 'Connectivity';
  }
  return 'System';
};

function DeviceConfig() {
  const { serialNumber } = useParams();
  const [selectedConfig, setSelectedConfig] = useState(CONFIG_PATHS[0]);
  const [configData, setConfigData] = useState('');
  const [savedConfigData, setSavedConfigData] = useState('');
  const [editorMode, setEditorMode] = useState('guided');
  const [messagesTooLong, setMessagesTooLong] = useState(false);
  const [isEditingJson, setIsEditingJson] = useState(false);
  const [syncPromptOpen, setSyncPromptOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const isGnssConfig = selectedConfig === 'gnss/gnss_ext_cfg';
  const isMneaStreamConfig = selectedConfig === 'gnss/nmea_stream';
  const isCanConfig = selectedConfig.startsWith('can/');
  const isAntennaLocationConfig = selectedConfig === 'gnss/antenna_location';
  const isNtripClientConfig = selectedConfig === 'gnss/ntrip_client';
  const isBluetoothConfig = selectedConfig === 'bluetooth';
  const hasGuidedEditor =
    isGnssConfig ||
    isMneaStreamConfig ||
    isCanConfig ||
    isAntennaLocationConfig ||
    isNtripClientConfig ||
    isBluetoothConfig;

  const fetchConfig = useCallback(async () => {
    if (!serialNumber || !selectedConfig) {
      return;
    }
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      const response = await getDeviceConfig(serialNumber, selectedConfig);
      const serialized = JSON.stringify(response.data, null, 2);
      setConfigData(serialized);
      setSavedConfigData(serialized);
    } catch {
      setError('Failed to load this configuration.');
      setConfigData('');
      setSavedConfigData('');
    } finally {
      setLoading(false);
    }
  }, [selectedConfig, serialNumber]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  useEffect(() => {
    if ((isGnssConfig || isMneaStreamConfig) && configData) {
      try {
        const config = JSON.parse(configData);
        const byteCount = new TextEncoder().encode(config.messages || '').length;
        setMessagesTooLong(byteCount > (isGnssConfig ? 500 : 90));
      } catch {
        setMessagesTooLong(false);
      }
    } else {
      setMessagesTooLong(false);
    }
  }, [configData, isGnssConfig, isMneaStreamConfig]);

  useEffect(() => {
    if (!hasGuidedEditor) {
      setEditorMode('json');
    }
  }, [hasGuidedEditor, selectedConfig]);

  const selectConfig = path => {
    setSelectedConfig(path);
    setConfigData('');
    setSavedConfigData('');
    setEditorMode('guided');
    setError('');
    setSuccess('');
  };

  const handleGuiConfigChange = value => {
    if (!isEditingJson) {
      setConfigData(value);
    }
  };

  const handleWrite = async () => {
    let parsedData;
    try {
      parsedData = JSON.parse(configData);
    } catch {
      setError('The JSON is invalid. Fix it before saving.');
      setEditorMode('json');
      return;
    }
    try {
      setSaving(true);
      setError('');
      await setDeviceConfig(serialNumber, selectedConfig, parsedData);
      setSavedConfigData(JSON.stringify(parsedData, null, 2));
      setConfigData(JSON.stringify(parsedData, null, 2));
      setSuccess(`${configLabel(selectedConfig)} saved.`);
      refreshUnsyncedConfigs(serialNumber);
      setSyncPromptOpen(true);
    } catch {
      setError('Failed to save configuration.');
    } finally {
      setSaving(false);
    }
  };

  const handleSyncAndReboot = () => {
    setSyncPromptOpen(false);
    setSuccess('');
    syncAndReboot(serialNumber);
  };

  const groupedConfigs = useMemo(() => {
    const groups = new Map();
    CONFIG_PATHS.forEach(path =>
      groups.set(configGroup(path), [...(groups.get(configGroup(path)) || []), path])
    );
    return [...groups.entries()];
  }, []);

  const renderGuidedEditor = () => {
    const props = { configData, onConfigChange: handleGuiConfigChange, isEditingJson };
    if (isGnssConfig) {
      return <GnssConfigEditor {...props} />;
    }
    if (isMneaStreamConfig) {
      return <MneaStreamConfigEditor {...props} />;
    }
    if (isCanConfig) {
      return <CanConfigEditor {...props} />;
    }
    if (isAntennaLocationConfig) {
      return <AntennaLocationConfigEditor {...props} />;
    }
    if (isNtripClientConfig) {
      return <NtripClientConfigEditor {...props} />;
    }
    if (isBluetoothConfig) {
      return <BluetoothConfigEditor {...props} />;
    }
    return null;
  };

  const dirty = Boolean(configData && configData !== savedConfigData);

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <span className={styles.eyebrow}>Hub tools</span>
          <h1>Configuration</h1>
          <p>Choose one subsystem, make a focused change, then save it to the selected hub.</p>
        </div>
        <div className={styles.headerActions}>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={fetchConfig}
            disabled={loading}
          >
            <RefreshCw size={15} className={loading ? styles.spinning : ''} /> Reload
          </button>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={handleWrite}
            disabled={saving || messagesTooLong || !dirty}
          >
            <Save size={15} /> {saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
          </button>
        </div>
      </header>
      {error && (
        <Toast tone="error" onClose={() => setError('')}>
          {error}
        </Toast>
      )}
      {success && (
        <Toast tone="success" onClose={() => setSuccess('')}>
          {success}
        </Toast>
      )}
      {messagesTooLong && (
        <Toast tone="error">
          Messages exceed the {isGnssConfig ? '500' : '90'} byte limit. Reduce the configured
          messages before saving.
        </Toast>
      )}

      <div className={styles.configWorkspace}>
        <aside className={styles.configRail}>
          <header>
            <SlidersHorizontal size={16} />
            <div>
              <strong>Subsystems</strong>
              <small>{CONFIG_PATHS.length} configurations</small>
            </div>
          </header>
          <select value={selectedConfig} onChange={event => selectConfig(event.target.value)}>
            {CONFIG_PATHS.map(path => (
              <option key={path} value={path}>
                {configLabel(path)}
              </option>
            ))}
          </select>
          <nav>
            {groupedConfigs.map(([group, paths]) => (
              <div key={group}>
                <span>{group}</span>
                {paths.map(path => (
                  <button
                    type="button"
                    key={path}
                    className={selectedConfig === path ? styles.configActive : ''}
                    onClick={() => selectConfig(path)}
                  >
                    {configLabel(path).replace(`${group} · `, '')}
                  </button>
                ))}
              </div>
            ))}
          </nav>
        </aside>

        <section className={styles.editorPanel}>
          <div className={styles.editorHeader}>
            <div>
              <span>{configGroup(selectedConfig)}</span>
              <h2>{configLabel(selectedConfig)}</h2>
            </div>
            {hasGuidedEditor && (
              <div className={styles.modeSwitcher}>
                <button
                  type="button"
                  className={editorMode === 'guided' ? styles.modeActive : ''}
                  onClick={() => setEditorMode('guided')}
                >
                  <SlidersHorizontal size={14} /> Guided
                </button>
                <button
                  type="button"
                  className={editorMode === 'json' ? styles.modeActive : ''}
                  onClick={() => setEditorMode('json')}
                >
                  <Code2 size={14} /> JSON
                </button>
              </div>
            )}
          </div>
          <div className={styles.editorBody}>
            {loading && !configData ? (
              <div className={styles.loadingState}>Loading configuration…</div>
            ) : editorMode === 'guided' && hasGuidedEditor ? (
              renderGuidedEditor()
            ) : (
              <textarea
                value={configData}
                onChange={event => setConfigData(event.target.value)}
                onFocus={() => setIsEditingJson(true)}
                onBlur={() => setIsEditingJson(false)}
                className={styles.jsonEditor}
                spellCheck={false}
              />
            )}
          </div>
        </section>
      </div>

      <ConfirmDialog
        open={syncPromptOpen}
        tone="primary"
        title="Sync & reboot the hub now?"
        description="The configuration is saved in the cloud. The hub applies it after it syncs and reboots, and it will be unavailable while it restarts."
        confirmLabel="Sync & Reboot"
        cancelLabel="Later"
        onConfirm={handleSyncAndReboot}
        onCancel={() => setSyncPromptOpen(false)}
      />
    </div>
  );
}

export default DeviceConfig;
